import {t} from "@lingui/core/macro";

import type {PhosphorIconAsset} from "@khanacademy/wonder-blocks-icon";
import {isTruthy} from "@khanacademy/wonder-stuff-core";

import plusIcon from "@phosphor-icons/core/bold/plus-bold.svg";
import arrowsClockwiseIcon from "@phosphor-icons/core/regular/arrows-clockwise.svg";
import chatsIcon from "@phosphor-icons/core/regular/chats.svg";
import pencilCircleIcon from "@phosphor-icons/core/regular/pencil-circle.svg";
import pencilIcon from "@phosphor-icons/core/regular/pencil.svg";
import plusCircleIcon from "@phosphor-icons/core/regular/plus-circle.svg";
import questionIcon from "@phosphor-icons/core/regular/question.svg";
import swapIcon from "@phosphor-icons/core/regular/swap.svg";

export type SelectedAction =
    | "CHAT_ABOUT_THIS"
    | "USE_A_SIMPLER_WORD_HERE"
    | "WHAT_DOES_THIS_MEAN"
    | "MAKE_CHANGES_TO_THIS"
    | "TRY_SOMETHING_DIFFERENT"
    | "NAME_THIS_SECTION"
    | "ADD_ANOTHER_ITEM"
    | "FILL_THIS_IN_FOR_ME";

export interface Suggestion {
    kind: "chat" | "change";
    text: () => string;
    icon: PhosphorIconAsset;
    needsAdditionalInput?: boolean;
    additionalText?: string;
    // Used for CEDAR event
    action: SelectedAction;
}

/**
 * A list of all the suggestions we support.
 * Each suggestion has a text label
 * and an icon.
 **/
export const suggestionList: Record<string, Suggestion> = {
    addContent: {
        kind: "change",
        text: () => t`Add content`,
        icon: plusCircleIcon,
        needsAdditionalInput: true,
        action: "FILL_THIS_IN_FOR_ME",
    },
    chatAboutThis: {
        kind: "chat",
        text: () => t`Discuss this`,
        icon: chatsIcon,
        action: "CHAT_ABOUT_THIS",
    },
    useSimplerWord: {
        kind: "change",
        text: () => t`Use a simpler word here`,
        icon: swapIcon,
        action: "USE_A_SIMPLER_WORD_HERE",
    },
    whatDoesThisMean: {
        kind: "chat",
        text: () => t`What does this mean?`,
        icon: questionIcon,
        action: "WHAT_DOES_THIS_MEAN",
    },
    makeChanges: {
        kind: "change",
        text: () => t`Make changes to this`,
        icon: pencilIcon,
        needsAdditionalInput: true,
        action: "MAKE_CHANGES_TO_THIS",
    },
    trySomethingDifferent: {
        kind: "change",
        text: () => t`Try something different`,
        icon: arrowsClockwiseIcon,
        action: "TRY_SOMETHING_DIFFERENT",
    },
    nameThisSection: {
        kind: "change",
        text: () => t`Name this section`,
        icon: pencilCircleIcon,
        action: "NAME_THIS_SECTION",
    },
    addAnotherItem: {
        kind: "change",
        text: () => t`Add another item`,
        icon: plusIcon,
        action: "ADD_ANOTHER_ITEM",
    },
    fillThisInForMe: {
        kind: "change",
        text: () => t`Fill this in for me`,
        icon: pencilCircleIcon,
        action: "FILL_THIS_IN_FOR_ME",
    },
} as const;

export const singleLongWordSuggestions: Suggestion[] = [
    suggestionList.useSimplerWord,
    suggestionList.whatDoesThisMean,
    suggestionList.chatAboutThis,
];

export const paragraphSuggestions: Suggestion[] = [
    suggestionList.makeChanges,
    suggestionList.trySomethingDifferent,
    suggestionList.chatAboutThis,
];

export const emptyBlockSuggestions: Suggestion[] = [suggestionList.addContent];

export const emptyHeadingSuggestions: Suggestion[] = [
    suggestionList.makeChanges,
    suggestionList.nameThisSection,
    suggestionList.chatAboutThis,
];

export const emptyListItemSuggestions: Suggestion[] = [
    suggestionList.makeChanges,
    suggestionList.addAnotherItem,
    suggestionList.fillThisInForMe,
    suggestionList.chatAboutThis,
];

/**
 * Determine suggestions to show within the coeditor document based on the
 * current selection and document contents.
 *
 * Note that the logic here is pretty rudimentary right now, especially for
 * determining what constitutes a "long word". We can definitely make this
 * logic smarter (and the suggestions more useful and context-aware) in the
 * future!
 */
export default function getSuggestionsForCurrentSelection(documentParts: {
    prefix: string;
    selection: string;
    suffix: string;
}): Suggestion[] {
    const {prefix, selection, suffix} = documentParts;

    const trimmedSelection = selection.trim();

    if (trimmedSelection && !trimmedSelection.includes(" ")) {
        if (trimmedSelection.length > 5) {
            // If we have something selected that doesn't contain any spaces
            // and is not super-short, consider it a single "long" word that.
            return singleLongWordSuggestions;
        }

        // Otherwise, no suggestions, to avoid triggering the popup for words
        // like "the"/"is"/etc.
        return [];
    }

    if (!trimmedSelection) {
        const suffixStartsNewBlock = suffix.startsWith("\n");

        if (suffixStartsNewBlock && prefix.endsWith("\n")) {
            const lastNonBlankLineOfPrefix =
                prefix.split(/\n+/).filter(isTruthy).at(-1) || "";

            if (/^#+ +/.test(lastNonBlankLineOfPrefix)) {
                const sectionName = lastNonBlankLineOfPrefix.replace(
                    /^#+ +/,
                    "",
                );

                return [
                    {
                        kind: "change",
                        text: () => t`Fill in “${sectionName}” for me`,
                        icon: pencilCircleIcon,
                        action: "FILL_THIS_IN_FOR_ME",
                    },
                ];
            }

            // We're on an empty block between other blocks
            return emptyBlockSuggestions;
        }

        if (
            suffixStartsNewBlock &&
            (prefix.endsWith("\n# ") || prefix.endsWith("\n## "))
        ) {
            // We're at the beginning of a heading block
            return emptyHeadingSuggestions;
        }

        if (
            suffixStartsNewBlock &&
            (prefix.endsWith("\n- ") ||
                prefix.endsWith("\n* ") ||
                prefix.endsWith("\n+ "))
        ) {
            // We're at the beginning of a list item
            return emptyListItemSuggestions;
        }

        // Otherwise, since we have no selection, we have no suggestions.
        return [];
    }

    // By default, give suggestions for improving larger blocks of text (e.g.
    // paragraphs).
    return paragraphSuggestions;
}
